import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../environments/environment';

export interface AdminStats {
  pending_listings: number;
  previous_actions: number;
  pending_contacts: number;
  total_contacts: number;
  urgent_contacts: number;
  contacts_today: number;
  avg_response_time: number;
}

export interface PendingListing {
  id: number;
  title: string;
  description: string;
  status: string;
  created_at: string;
  updated_at: string;
  view_count: number;
  images: string[];
  decline_reason?: string;
  first_name: string;
  last_name: string;
  email: string;
  category: string;
  subcategory: string;
  table_name: string;
  image_count: number;
  first_image: string | null;
}

export interface PendingListingsResponse {
  message: string;
  listings: PendingListing[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export interface PendingListingsFilters {
  category?: string;
  status?: string;
  search?: string;
  dateFrom?: string;
  dateTo?: string;
}

export interface AdminAction {
  id: string;
  admin_id: number;
  action_type: string;
  target_type: string;
  target_id?: string;
  details: any;
  metadata: any;
  created_at: string;
  admin_first_name: string;
  admin_last_name: string;
  admin_email: string;
}

export interface AdminActionsResponse {
  message: string;
  actions: AdminAction[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export interface AdminActionHistory {
  id: string;
  action_type: string;
  target_type: string;
  target_id: string;
  details: any;
  metadata: any;
  created_at: string;
  can_edit: boolean;
}

export interface AdminActionHistoryResponse {
  message: string;
  actions: AdminActionHistory[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export interface LatestActionInfo {
  id: string;
  action_type: string;
  created_at: string;
  admin_name: string;
}

export interface NotLatestStatusError {
  message: string;
  errorType: 'not_latest_status';
  latestAction: LatestActionInfo;
}

export interface ContactSubmission {
  id: string;
  name: string;
  email: string;
  phone?: string;
  subject?: string;
  message: string;
  category?: string;
  status: 'pending' | 'in_progress' | 'resolved' | 'closed';
  priority: 'low' | 'normal' | 'high' | 'urgent';
  assigned_to?: string;
  resolved_by?: string;
  response_count: number;
  responded_at?: string;
  resolved_at?: string;
  resolution_notes?: string;
  internal_notes?: string;
  ip_address?: string;
  user_agent?: string;
  request_id?: string;
  user_id?: string;
  created_at: string;
  updated_at: string;
  assigned_admin_first_name?: string;
  assigned_admin_last_name?: string;
  resolved_admin_first_name?: string;
  resolved_admin_last_name?: string;
  last_admin_response?: string;
  last_user_response?: string;
}

export interface ContactSubmissionResponse {
  id: string;
  contact_submission_id: string;
  admin_id: string | null;
  user_id?: string | null;
  user_name?: string;
  user_email?: string;
  response_text: string;
  response_type: 'email' | 'phone' | 'internal_note' | 'user_reply';
  is_sent_to_customer: boolean;
  email_subject?: string;
  email_sent_successfully?: boolean;
  email_error_message?: string;
  created_at: string;
  updated_at: string;
  admin_first_name?: string;
  admin_last_name?: string;
  admin_email?: string;
  response_author_type?: 'admin' | 'user' | 'user_legacy' | 'system';
  author_name?: string;
  clean_response_text?: string;
}

export interface ContactSubmissionDetails {
  submission: ContactSubmission;
  responses: ContactSubmissionResponse[];
}

export interface ContactSubmissionsResponse {
  message: string;
  data: {
    submissions: ContactSubmission[];
    pagination: {
      currentPage: number;
      totalPages: number;
      totalItems: number;
      itemsPerPage: number;
      hasNextPage: boolean;
      hasPreviousPage: boolean;
    };
  };
}

export interface ContactSubmissionsFilters {
  status?: string;
  priority?: string;
  search?: string;
  assignedTo?: string;
}

export interface ContactStats {
  total: number;
  pending: number;
  in_progress: number;
  resolved: number;
  closed: number;
  urgent: number;
  high: number;
  today: number;
  this_week: number;
  avg_resolution_hours?: number;
}

export interface AdminUser {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
}

export interface RelistRefundInfo {
  listingId: string;
  title: string;
  category: string;
  subcategory: string;
  previousCount: number;
  newCount: number;
}

export interface DeclineResult {
  id: string;
  success: boolean;
  listing?: any;
  error?: string;
  used_free_relist?: boolean;
  relist_refunded?: boolean;
  refund_info?: RelistRefundInfo;
}

export interface DeclineResponse {
  message: string;
  results: DeclineResult[];
  summary: {
    total: number;
    declined: number;
    failed: number;
    relist_credits_refunded: number;
  };
}

export interface ApprovalResult {
  id: string;
  success: boolean;
  listing?: any;
  error?: string;
  payment_success?: boolean;
  payment_amount?: number;
  updated_balance?: number;
  used_free_relist?: boolean;
}

export interface ApprovalResponse {
  message: string;
  results: ApprovalResult[];
  payment_failures?: any[];
  summary: {
    total: number;
    approved: number;
    failed: number;
    payment_failures: number;
  };
}

export interface RAGUnembeddedCountResponse {
  status: string;
  totalUnembedded: number;
  estimatedCost: string;
  recommendedBatches: number;
  safetyLimit: number;
  withinSafetyLimit: boolean;
  timestamp: string;
}

export interface RAGProcessResponse {
  status: string;
  message: string;
  totalFound: number;
  totalProcessed: number;
  tablesProcessed: number;
  tablesWithData: number;
  processedAll: boolean;
  estimatedCost: string;
  timestamp: string;
}

@Injectable({
  providedIn: 'root',
})
export class AdminService {
  private apiUrl = environment.apiUrl || 'http://localhost:3000/api/v1';

  constructor(private http: HttpClient) {}

  getDashboardStats(): Observable<AdminStats> {
    return this.http
      .get<{ message: string; stats: AdminStats }>(
        `${this.apiUrl}/admin/dashboard`
      )
      .pipe(
        map((response) => response.stats),
        catchError(this.handleError)
      );
  }

  getPendingListings(
    filters: PendingListingsFilters = {},
    page: number = 1,
    limit: number = 20
  ): Observable<PendingListingsResponse> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString());

    Object.keys(filters).forEach((key) => {
      const value = filters[key as keyof PendingListingsFilters];
      if (value !== undefined && value !== null && value !== '') {
        params = params.set(key, value);
      }
    });

    return this.http
      .get<PendingListingsResponse>(`${this.apiUrl}/admin/listings/pending`, {
        params,
      })
      .pipe(catchError(this.handleError));
  }

  getPendingListingDetail(
    category: string,
    subcategory: string,
    listingId: string
  ): Observable<any> {
    return this.http
      .get<any>(
        `${this.apiUrl}/admin/listings/${category}/${subcategory}/${listingId}`
      )
      .pipe(catchError(this.handleError));
  }

  editPendingListing(
    category: string,
    subcategory: string,
    listingId: string,
    updateData: any
  ): Observable<any> {
    return this.http
      .put<any>(
        `${this.apiUrl}/admin/listings/${category}/${subcategory}/${listingId}`,
        updateData
      )
      .pipe(catchError(this.handleError));
  }

  approveListings(listingIds: string[], note?: string): Observable<any> {
    const payload: any = { listingIds };
    if (note) {
      payload.note = note;
    }

    return this.http
      .post(`${this.apiUrl}/admin/listings/approve`, payload)
      .pipe(catchError(this.handleError));
  }

  declineListings(
    listingIds: string[],
    reason: string
  ): Observable<DeclineResponse> {
    return this.http
      .post<DeclineResponse>(`${this.apiUrl}/admin/listings/decline`, {
        listingIds,
        reason,
      })
      .pipe(catchError(this.handleError));
  }

  bulkApproveAll(category: string = 'all', note?: string): Observable<any> {
    const payload: any = { category };
    if (note) {
      payload.note = note;
    }

    return this.http
      .post(`${this.apiUrl}/admin/listings/bulk-approve`, payload)
      .pipe(catchError(this.handleError));
  }

  getAdminActions(
    filters: any = {},
    page: number = 1,
    limit: number = 50
  ): Observable<AdminActionsResponse> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString());

    Object.keys(filters).forEach((key) => {
      const value = filters[key];
      if (value !== undefined && value !== null && value !== '') {
        params = params.set(key, value);
      }
    });

    return this.http
      .get<AdminActionsResponse>(`${this.apiUrl}/admin/actions`, { params })
      .pipe(catchError(this.handleError));
  }

  getAdminActionHistory(
    page: number = 1,
    limit: number = 50
  ): Observable<AdminActionHistoryResponse> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString());

    return this.http
      .get<AdminActionHistoryResponse>(`${this.apiUrl}/admin/actions/history`, {
        params,
      })
      .pipe(catchError(this.handleError));
  }

  updateAdminAction(
    actionId: string,
    newAction: string,
    reason?: string
  ): Observable<any> {
    const payload: any = { newAction };
    if (reason) {
      payload.reason = reason;
    }

    return this.http
      .put(`${this.apiUrl}/admin/actions/${actionId}`, payload)
      .pipe(
        catchError((error) => {
          if (
            error.status === 400 &&
            error.error?.error_type === 'not_latest_status'
          ) {
            const notLatestError: NotLatestStatusError = {
              message: error.error.message,
              errorType: 'not_latest_status',
              latestAction: error.error.latest_action,
            };
            return throwError(() => notLatestError);
          }
          return this.handleError(error);
        })
      );
  }

  getContactSubmissions(
    filters: ContactSubmissionsFilters = {},
    page: number = 1,
    limit: number = 20
  ): Observable<ContactSubmissionsResponse> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString());

    Object.keys(filters).forEach((key) => {
      const value = filters[key as keyof ContactSubmissionsFilters];
      if (value !== undefined && value !== null && value !== '') {
        params = params.set(key, value);
      }
    });

    return this.http
      .get<ContactSubmissionsResponse>(`${this.apiUrl}/admin/contact`, {
        params,
      })
      .pipe(catchError(this.handleError));
  }

  getContactSubmissionById(
    submissionId: string
  ): Observable<ContactSubmissionDetails> {
    return this.http
      .get<{ message: string; data: ContactSubmissionDetails }>(
        `${this.apiUrl}/admin/contact/${submissionId}`
      )
      .pipe(
        map((response) => response.data),
        catchError(this.handleError)
      );
  }

  updateContactSubmission(
    submissionId: string,
    updates: Partial<ContactSubmission>
  ): Observable<ContactSubmission> {
    return this.http
      .patch<{ message: string; data: ContactSubmission }>(
        `${this.apiUrl}/admin/contact/${submissionId}`,
        updates
      )
      .pipe(
        map((response) => response.data),
        catchError(this.handleError)
      );
  }

  addContactResponse(
    submissionId: string,
    responseData: {
      responseText: string;
      responseType?: 'email' | 'phone' | 'internal_note';
      isSentToCustomer?: boolean;
      emailSubject?: string;
    }
  ): Observable<ContactSubmissionResponse> {
    return this.http
      .post<{ message: string; data: ContactSubmissionResponse }>(
        `${this.apiUrl}/admin/contact/${submissionId}/responses`,
        responseData
      )
      .pipe(
        map((response) => response.data),
        catchError(this.handleError)
      );
  }

  getContactStats(): Observable<ContactStats> {
    return this.http
      .get<{ message: string; data: ContactStats }>(
        `${this.apiUrl}/admin/contact/stats`
      )
      .pipe(
        map((response) => response.data),
        catchError(this.handleError)
      );
  }

  getAvailableAdmins(): Observable<AdminUser[]> {
    return this.http
      .get<{ message: string; data: AdminUser[] }>(
        `${this.apiUrl}/admin/contact/admins`
      )
      .pipe(
        map((response) => response.data),
        catchError(this.handleError)
      );
  }

  bulkUpdateContactSubmissions(
    submissionIds: string[],
    updates: Partial<ContactSubmission>
  ): Observable<any> {
    const payload = {
      submissionIds,
      updates,
    };

    return this.http
      .patch(`${this.apiUrl}/admin/contact/bulk-update`, payload)
      .pipe(
        catchError((error) => {
          console.error('Service - HTTP error:', error);
          return this.handleError(error);
        })
      );
  }

  getUnembeddedCount(
    tables?: string[],
    skipExisting: boolean = true
  ): Observable<RAGUnembeddedCountResponse> {
    let params = new HttpParams().set('skipExisting', skipExisting.toString());

    if (tables && tables.length > 0) {
      params = params.set('tables', tables.join(','));
    }

    return this.http
      .get<RAGUnembeddedCountResponse>(`${this.apiUrl}/rag/unembedded-count`, {
        params,
      })
      .pipe(catchError(this.handleError));
  }

  processUnembeddedListings(
    processAll: boolean = false
  ): Observable<RAGProcessResponse> {
    const payload = { processAll };

    return this.http
      .post<RAGProcessResponse>(
        `${this.apiUrl}/rag/process-unembedded`,
        payload
      )
      .pipe(catchError(this.handleError));
  }

  formatDate(dateString: string): string {
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffTime = Math.abs(now.getTime() - date.getTime());
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      if (diffDays === 0) {
        return 'Today';
      } else if (diffDays === 1) {
        return 'Yesterday';
      } else if (diffDays < 7) {
        return `${diffDays} days ago`;
      } else {
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        });
      }
    } catch (error) {
      return 'Unknown date';
    }
  }

  formatPriority(priority: string): string {
    switch (priority.toLowerCase()) {
      case 'urgent':
        return 'Urgent';
      case 'high':
        return 'High';
      case 'normal':
        return 'Normal';
      case 'low':
        return 'Low';
      default:
        return priority;
    }
  }

  formatStatus(status: string): string {
    switch (status.toLowerCase()) {
      case 'pending':
        return 'Pending';
      case 'in_progress':
        return 'In Progress';
      case 'resolved':
        return 'Resolved';
      case 'closed':
        return 'Closed';
      case 'active':
        return 'Active';
      case 'declined':
        return 'Declined';
      default:
        return status;
    }
  }

  formatActionType(actionType: string): string {
    switch (actionType) {
      case 'approve_listing':
        return 'Approved Listing';
      case 'decline_listing':
        return 'Declined Listing';
      case 'approve_listings':
        return 'Bulk Approved';
      case 'decline_listings':
        return 'Bulk Declined';
      case 'bulk_approve_all':
        return 'Bulk Approve All';
      case 'respond':
        return 'Responded to Contact';
      case 'update':
        return 'Updated Contact';
      default:
        return actionType
          .replace(/_/g, ' ')
          .replace(/\b\w/g, (l) => l.toUpperCase());
    }
  }

  canEditAction(action: AdminActionHistory): boolean {
    if (!action.can_edit) return false;

    const actionDate = new Date(action.created_at);
    const now = new Date();
    const daysDiff = Math.floor(
      (now.getTime() - actionDate.getTime()) / (1000 * 60 * 60 * 24)
    );

    return daysDiff <= 7;
  }

  getDeclineReason(listing: PendingListing): string {
    return listing.decline_reason || 'No reason provided';
  }

  getPriorityClass(priority: string): string {
    switch (priority.toLowerCase()) {
      case 'urgent':
        return 'priority-urgent';
      case 'high':
        return 'priority-high';
      case 'normal':
        return 'priority-normal';
      case 'low':
        return 'priority-low';
      default:
        return 'priority-normal';
    }
  }

  getStatusClass(status: string): string {
    switch (status.toLowerCase()) {
      case 'pending':
        return 'status-pending';
      case 'in_progress':
        return 'status-in-progress';
      case 'resolved':
        return 'status-resolved';
      case 'closed':
        return 'status-closed';
      case 'active':
        return 'status-active';
      case 'declined':
        return 'status-declined';
      default:
        return 'status-default';
    }
  }

  private handleError(error: any): Observable<never> {
    let errorMessage = 'An error occurred';

    if (error.error?.message) {
      errorMessage = error.error.message;
    } else if (error.status === 403) {
      errorMessage = 'Access denied. Admin privileges required.';
    } else if (error.status === 401) {
      errorMessage = 'Authentication required. Please log in again.';
    } else if (error.status === 404) {
      errorMessage = 'Resource not found.';
    } else if (error.status === 500) {
      errorMessage = 'Server error. Please try again later.';
    }

    console.error('Admin service error:', error);
    return throwError(() => new Error(errorMessage));
  }
}
